import { useQuery } from "@tanstack/react-query";
import { Users } from "lucide-react";
import { Link, useNavigate } from "react-router";
import { api } from "../api";
import { EmptyState, PageHeader } from "../components/page";
import { CreateTeamDialog } from "../components/teams/create-team-dialog";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { personLabel } from "../lib/people";
import { useIsManager } from "../lib/permissions";

export function TeamsPage() {
  const navigate = useNavigate();
  const manager = useIsManager();
  const query = useQuery({ queryKey: ["teams"], queryFn: api.teams });
  const teams = query.data?.teams ?? [];

  return (
    <>
      <PageHeader
        title="Teams"
        description="Who owns what. Issues are routed to the teams their reporter and reviewer belong to."
        actions={manager ? <CreateTeamDialog /> : undefined}
      />
      {query.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-40 rounded-xl" />
          ))}
        </div>
      ) : teams.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No teams yet"
          description={
            manager
              ? "Create a team, add members and approvers, then connect its Slack channel."
              : "Teams show up here once an admin or manager creates them."
          }
        >
          {manager && <CreateTeamDialog />}
        </EmptyState>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {teams.map((team) => (
            <Card
              key={team.id}
              className="cursor-pointer gap-3 transition-[transform,box-shadow] duration-200 ease-calm hover:-translate-y-0.5 hover:shadow-md motion-reduce:transform-none"
              onClick={() => navigate(`/teams/${team.key}`)}
            >
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className="size-3 rounded-sm" style={{ backgroundColor: team.color }} />
                  {team.name}
                  <span className="font-mono text-xs text-muted-foreground">{team.key}</span>
                </CardTitle>
                {team.description && <CardDescription>{team.description}</CardDescription>}
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-1.5">
                  {team.members.slice(0, 6).map((member) => (
                    <Avatar key={member.id} className="size-6">
                      <AvatarFallback className="text-[10px]">
                        {personLabel(member)
                          .split(/\s+/)
                          .map((part) => part[0])
                          .slice(0, 2)
                          .join("")
                          .toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  ))}
                  {team.members.length === 0 && (
                    <span className="text-xs text-muted-foreground">No members yet</span>
                  )}
                  {team.members.length > 6 && (
                    <span className="text-xs text-muted-foreground">+{team.members.length - 6}</span>
                  )}
                </div>
                {team.slackChannel && (
                  <p className="text-xs text-muted-foreground">Slack: {team.slackChannel}</p>
                )}
                <Link
                  to={`/issues?team=${team.key}`}
                  onClick={(e) => e.stopPropagation()}
                  className="text-xs text-muted-foreground hover:text-foreground hover:underline"
                >
                  {team.issueCount} issue{team.issueCount === 1 ? "" : "s"}
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}
